import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import TestimonialForm from "@/pages/profile/components/TestimonialForm";
import MarcaEntreElas from "@/components/feature/MarcaEntreElas";

export default function Depoimento() {
  const { isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberName, setMemberName] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function checkMember() {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      let member = null;
      try {
        const { data } = await supabase.rpc("my_member_profile");
        member = data;
      } catch {
        member = null;
      }

      if (!cancelled) {
        if (member) {
          setMemberId(String(member.id));
          setMemberName(member.full_name || "");
        }
        setLoading(false);
      }
    }

    checkMember();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ---------------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown">Carregando...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------
  // NOT A MEMBER (or not logged in)
  // ---------------------------------------------------------------
  if (!memberId) {
    return (
      <div className="min-h-screen bg-beige">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="flex items-center justify-between h-16 md:h-20">
              <MarcaEntreElas />
              <div className="hidden md:flex items-center gap-6">
                <Link to="/comunidade" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                  Comunidade
                </Link>
                {isAuthenticated ? (
                  <button onClick={logout} className="text-sm font-body text-brown/50 tracking-wide uppercase transition-colors duration-300 hover:text-red-500 whitespace-nowrap cursor-pointer">
                    Sair
                  </button>
                ) : (
                  <Link to="/meu-perfil" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                    Entrar
                  </Link>
                )}
                <Link to="/cadastro" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                  Cadastro
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* CTA Section */}
        <section className="pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-accent-50 via-white to-accent-50 rounded-2xl p-8 md:p-12 text-center border border-accent-200/50">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-5">
                <i className="ri-chat-quote-line text-accent-600 text-2xl" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                {isAuthenticated
                  ? "Você ainda não faz parte da comunidade"
                  : "Você foi convidada a deixar seu depoimento!"}
              </h2>
              <p className="font-body text-brown/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto mb-8">
                {isAuthenticated
                  ? "Seu email foi autenticado, mas não encontramos um cadastro aprovado vinculado a ele. Cadastre-se na comunidade Entre Elas para deixar seu depoimento e fazer parte dessa rede de mulheres empreendedoras."
                  : "A comunidade Entre Elas quer ouvir sua voz. Cadastre-se gratuitamente para deixar seu depoimento e fazer parte dessa rede de mulheres que se apoiam e crescem juntas."}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                {!isAuthenticated && (
                  <Link
                    to="/meu-perfil"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white border-2 border-primary text-primary font-body text-sm tracking-wide uppercase hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                  >
                    <i className="ri-login-box-line" />
                    Fazer login
                  </Link>
                )}
                <Link
                  to="/cadastro"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  <i className="ri-user-add-line" />
                  Cadastre-se gratuitamente
                </Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="bg-brown py-8">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto text-center">
            <p className="font-body text-sm text-white/60">
              &copy; {new Date().getFullYear()} Entre Elas. Todos os direitos reservados.
            </p>
            <p className="font-body text-xs text-white/40 mt-1">
              Uma comunidade de mulheres que se apoiam e crescem juntas.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // ---------------------------------------------------------------
  // MEMBER LOGGED IN — show testimonial form directly
  // ---------------------------------------------------------------
  return (
    <div className="min-h-screen bg-beige">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            <MarcaEntreElas />
            <div className="hidden md:flex items-center gap-6">
              <Link to="/comunidade" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                Comunidade
              </Link>
              <Link to={`/perfil/${memberId}`} className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                Meu Perfil
              </Link>
              <button onClick={logout} className="text-sm font-body text-brown/50 tracking-wide uppercase transition-colors duration-300 hover:text-red-500 whitespace-nowrap cursor-pointer">
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Page header */}
      <section className="pt-20 md:pt-28 pb-4">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto text-center">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-2">
            Comunidade Entre Elas
          </p>
          <h2 className="font-display text-2xl md:text-3xl text-brown mb-3">
            Olá, {memberName}!
          </h2>
          <p className="font-body text-sm text-brown/60 max-w-lg mx-auto">
            Você foi convidada a compartilhar seu depoimento sobre a comunidade. Conte como o Entre Elas impactou sua vida e inspire outras mulheres!
          </p>
        </div>
      </section>

      {/* Testimonial form */}
      <TestimonialForm memberId={memberId} />

      {/* Footer */}
      <footer className="bg-brown py-8">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto text-center">
          <p className="font-body text-sm text-white/60">
            &copy; {new Date().getFullYear()} Entre Elas. Todos os direitos reservados.
          </p>
          <p className="font-body text-xs text-white/40 mt-1">
            Uma comunidade de mulheres que se apoiam e crescem juntas.
          </p>
        </div>
      </footer>
    </div>
  );
}